/**
 * «Воронка» (этап 3) — вызовы GiperCRM. Каждый адрес сверен с кодом CRM
 * (GiperCRM/server/src/routes/*), ссылки — в комментариях у функций.
 */
import { crmFetch } from './crm';
import type { CardPerson, CardStage } from './crmCard';

// ── Воронки ──

export type PipelineAccess = 'all' | 'shared' | 'branch' | 'open' | 'deals';

export interface PipelineItem {
  id: string;
  name: string;
  projectId?: string | null;
  isDefault?: boolean;
  position: number;
  stages: CardStage[];
  _count?: { deals: number };
  /**
   * Поля ниже отдаёт CRM начиная с ветки feat/pipeline-person-access (11.10.2026).
   * Старый сервер их не присылает — экран обходится без них.
   */
  access?: PipelineAccess;
  /** Сколько сделок воронки видно МНЕ. */
  visibleDeals?: number;
  departments?: Array<{ id: string; name: string | null }>;
  memberCount?: number;
}

/** GET /api/crm/pipelines — доступные спрашивающему воронки (routes/pipelines.ts, правило domain/visibility.pipelineAccess). */
export const getPipelines = () => crmFetch<PipelineItem[]>('/api/crm/pipelines');

export interface HomePipeline {
  pipelineId: string | null;
  pipelineName: string | null;
  source: string;
}

/** GET /api/crm/sales/home-pipeline — основная воронка (ближайший по схеме отдел, иначе общая основная; routes/sales.ts ~134). */
export const getHomePipeline = () => crmFetch<HomePipeline>('/api/crm/sales/home-pipeline');

// ── Сделки ──

export interface DealCallStats {
  total: number;
  missed: number;
  /** Пропущенных входящих после последнего разговора (14 дней) — «клиент звонил, перезвоните». */
  missedIncoming: number;
  missedIncomingAt: string | null;
}

export interface DealNextTask {
  title: string;
  /** Время (ISO) или день ГГГГ-ММ-ДД (задача на весь день). */
  at: string;
  allDay: boolean;
  overdue: boolean;
}

export interface DealRowData {
  id: string;
  title: string;
  amount?: string | number | null;
  currency?: string | null;
  stageId: string;
  pipelineId: string;
  ownerId?: string | null;
  source?: string | null;
  closedAt?: string | null;
  updatedAt?: string;
  awaitingReplySince?: string | null;
  chatwootAccountId?: number | null;
  chatwootDisplayId?: number | null;
  chatwootConversationId?: number | null;
  stage?: CardStage | null;
  person?: (CardPerson & { phones?: string[] | null }) | null;
  company?: { id: string; name: string } | null;
  callStats?: DealCallStats | null;
  /** Ближайшая невыполненная задача (CRM с 11.10.2026; старый сервер не присылает). */
  nextTask?: DealNextTask | null;
}

export interface StageTotal { stageId: string; currency: string; amount: number; count: number }

export interface DealListResponse {
  data: DealRowData[];
  total: number;
  page: number;
  limit: number;
  /** По этапам выбранной воронки (без отбора по этапу, но с остальными отборами). */
  stageTotals: StageTotal[];
  notes?: string[];
  /** Область видимости: all — администратор, branch — руководитель, own — рядовой. */
  scope?: { kind: 'all' | 'branch' | 'own'; label: string };
}

/** GET /api/crm/deals?… — список сделок раздела «Продажи» (routes/deals.ts, domain/dealListQuery.parseDealListQuery). */
export const listDeals = (params: URLSearchParams) => crmFetch<DealListResponse>(`/api/crm/deals?${params.toString()}`);

export interface DealFull extends DealRowData {
  pipeline?: { id: string; name: string; stages: CardStage[] } | null;
  description?: string | null;
  priority?: string | null;
  leadTemperature?: string | null;
  expectedCloseDate?: string | null;
}

/** GET /api/crm/deals/:id — сделка с воронкой и её этапами (routes/deals.ts ~168). Чужая — 404. */
export const getDeal = (id: string) => crmFetch<DealFull>(`/api/crm/deals/${encodeURIComponent(id)}`);

/**
 * POST /api/crm/deals/:id/qualify — «Принять» лид из «Новых» (routes/deals.ts ~887; веб: LeadActions.tsx).
 * Ведёт на первый рабочий этап; ничейный лид становится моим, назначенный — остаётся за назначенным.
 * Правила входа на этап могут не пустить: причина приходит текстом.
 */
export const qualifyDeal = (id: string) =>
  crmFetch<unknown>(`/api/crm/deals/${encodeURIComponent(id)}/qualify`, { method: 'POST', body: JSON.stringify({}) });

// ── Источники лидов ──

export interface LeadSource { id: string; key: string; name: string; active: boolean; pipeline_id: string | null }

/** GET /api/crm/lead-sources → { items } — справочник источников (routes/leadSources.ts ~110). */
export async function getLeadSources(): Promise<LeadSource[]> {
  const r = await crmFetch<{ items: LeadSource[] }>('/api/crm/lead-sources');
  return (r.items ?? []).filter((s) => s.active);
}

// ── Живые события ──

/** POST /api/crm/events/ticket — одноразовый пропуск на поток событий (routes/events.ts). */
export const issueEventsTicket = () => crmFetch<{ ticket: string; expiresIn: number }>('/api/crm/events/ticket', { method: 'POST' });
